import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Phone, Instagram, Facebook, Download, Sparkles, X } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import SiteFooter from './components/SiteFooter';

import { sendServerSideEvent, trackEventDedup } from './lib/tracking';
import { useScroll90, useViewOnce } from './hooks/useTracking';
import {
  ImagePlaceholder,
  ProductImage,
  PrimaryCTA,
  SecondaryCTA,
  MenuLink,
} from './components/CTAButtons';
import LeadFormModal from './components/LeadFormModal';
import ComparisonTable from './components/ComparisonTable';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useT } from './i18n/LanguageContext';

const IMG = {
  hero: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/lk362ars_Progetto%20senza%20titolo.jpg',
  detailDop: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/7f5p7ewr_Progetto%20senza%20titolo%20%282%29.jpg',
  rotoli: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/kbfr5bja_Progetto%20senza%20titolo%20%283%29.jpg',
  tacos: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/poe58oxw_Progetto%20senza%20titolo%20%284%29.jpg',
  piadine: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/vehs674y_Progetto%20senza%20titolo%20%281%29.jpg',
  crescioni: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/4phkvvav_Progetto%20senza%20titolo%20%285%29.jpg',
  segreto: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/7d9cvcoi_Progetto%20senza%20titolo%20%286%29.jpg',
};

const PDF = {
  piadine: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/2imzup6q_Men%C3%B9%20Sfarin%C3%A0%20-%20Piadine.pdf',
  crescioni: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/hov8ngpe_Men%C3%B9%20Sfarin%C3%A0%20-%20Solo%20Crescioni.pdf',
  rotoli: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/777gm6sh_Men%C3%B9%20Sfarin%C3%A0%20-%20solo%20rotoli.pdf',
  tacos: 'https://customer-assets.emergentagent.com/job_gourmet-lead-gen/artifacts/h2uj91az_Men%C3%B9%20Sfarin%C3%A0%20-%20French%20Tacos.pdf',
};

// Componente orari estratto a top-level (evita unstable-nested-components con React 19)
const SfarinaHours = ({ days, dayLabels, todayLabel }) => {
  const todayIdx = new Date().getDay();
  const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][todayIdx];
  return (
    <ul className="divide-y divide-[#E0D5C1]/60">
      {days.map(({ d, h }) => {
        const isToday = d === dayKey;
        return (
          <li
            key={d}
            data-testid={`hours-row-${d}`}
            className={`flex justify-between items-center gap-3 py-2.5 ${isToday ? '-mx-2 px-2 rounded-md bg-[#b03121]/10 border border-[#b03121]/25' : ''}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className={`font-['Poppins',_sans-serif] text-[13px] uppercase tracking-wider ${isToday ? 'text-[#b03121] font-bold' : 'text-[#5a4a42] font-semibold'}`}>
                {dayLabels[d]}
              </span>
              {isToday && (
                <span className="bg-[#b03121] text-white text-[9px] font-['Poppins',_sans-serif] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0">{todayLabel}</span>
              )}
            </span>
            <span className={`font-mono text-[13px] tabular-nums text-right whitespace-nowrap ${isToday ? 'text-[#b03121] font-bold' : 'text-[#1A1A1A] font-medium'}`}>{h}</span>
          </li>
        );
      })}
    </ul>
  );
};

export default function App() {
  const { t, lang } = useT();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSource, setModalSource] = useState('');

  const segretoRef = useRef(null);
  const sediRef = useRef(null);
  const ctaFinalRef = useRef(null);

  useEffect(() => {
    trackEventDedup('PageView', { context: 'initial_load' });
  }, [trackEventDedup]);

  const onScroll90 = useCallback(() => sendServerSideEvent('Scroll90', { depth_percent: 90 }), [sendServerSideEvent]);
  useScroll90(onScroll90);
  useViewOnce(segretoRef, useCallback(() => sendServerSideEvent('ViewSection', { section: 'segreto' }), [sendServerSideEvent]), 0.35);
  useViewOnce(sediRef, useCallback(() => sendServerSideEvent('ViewSection', { section: 'sedi' }), [sendServerSideEvent]), 0.35);
  useViewOnce(ctaFinalRef, useCallback(() => sendServerSideEvent('ViewSection', { section: 'cta_finale' }), [sendServerSideEvent]), 0.35);

  const openModal = useCallback((source) => {
    setModalSource(source);
    setModalOpen(true);
    sendServerSideEvent('ButtonClick', { button_label: t.nav.cta_discount, source });
  }, [t.nav.cta_discount, sendServerSideEvent]);

  const trackClick = (label, extra = {}) => () => {
    sendServerSideEvent('ButtonClick', { button_label: label, ...extra });
  };

  const trackPdfClick = (kind) => () => {
    sendServerSideEvent('ButtonClick', { button_label: 'menu-pdf-' + kind, source: 'menu-dettaglio', target: kind });
  };

  return (
    <div className="font-['Lora',_serif] bg-[#FDFBF7] text-[#1A1A1A] min-h-screen scroll-smooth" lang={lang}>
      <style>{`
        .text-highlight { color: #b03121; font-weight: 700; }
        .cta-primary { animation: pulse-soft 2s infinite; }
        @keyframes pulse-soft {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(176, 49, 33, 0.4); }
          70% { transform: scale(1.02); box-shadow: 0 0 0 15px rgba(176, 49, 33, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(176, 49, 33, 0); }
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>

      {/* NAVBAR */}
      <nav className="fixed w-full bg-[#fcfaf6]/95 backdrop-blur-md z-50 border-b border-[#f5efe6] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 gap-3">
            <span className="font-['Poppins',_sans-serif] font-black text-2xl md:text-3xl text-[#b03121] tracking-tighter" data-testid="nav-logo">SFARINÀ.</span>

            <div className="hidden lg:flex items-center space-x-4">
              <a href="#menu" onClick={trackClick('nav-menu')} data-testid="nav-link-menu" className="font-['Poppins',_sans-serif] font-semibold text-sm hover:text-[#b03121] transition-colors">{t.nav.menu}</a>
              <a href="#segreto" onClick={trackClick('nav-segreto')} data-testid="nav-link-segreto" className="font-['Poppins',_sans-serif] font-semibold text-sm hover:text-[#b03121] transition-colors">{t.nav.segreto}</a>
              <a href="#sedi" onClick={trackClick('nav-sedi')} data-testid="nav-link-sedi" className="font-['Poppins',_sans-serif] font-semibold text-sm hover:text-[#b03121] transition-colors">{t.nav.sedi}</a>
              <PrimaryCTA onClick={() => openModal('navbar')} className="py-2 px-6 rounded-full text-sm" testid="nav-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
              <LanguageSwitcher />
            </div>

            <div className="lg:hidden">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        {/* Sub-bar: 4 Landing Page links per advertising — sempre visibili, scroll orizzontale su mobile */}
        <div className="border-t border-[#f5efe6] bg-[#fcfaf6]/85" data-testid="lp-subbar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Le nostre proposte" className="flex items-center gap-1 sm:gap-2 md:gap-3 overflow-x-auto whitespace-nowrap py-2 sm:justify-center scrollbar-none" style={{ scrollbarWidth: 'none' }}>
              <span aria-disabled="true" data-testid="lp-subbar-degustazione-disabled" title="Presto disponibile"
                className="shrink-0 inline-flex items-center font-['Poppins',_sans-serif] font-semibold text-xs sm:text-sm text-gray-400 line-through cursor-not-allowed px-3 py-1.5 rounded-full border border-transparent select-none">
                Degustazioni
              </span>
              <span className="text-[#E0D5C1] select-none shrink-0" aria-hidden="true">·</span>
              <RouterLink to="/lp/mortadella-igp" onClick={trackClick('lp-subbar-mortadella')} data-testid="lp-subbar-mortadella"
                className="shrink-0 inline-flex items-center font-['Poppins',_sans-serif] font-semibold text-xs sm:text-sm text-[#1A1A1A] hover:text-[#b03121] hover:bg-[#b03121]/5 transition-colors px-3 py-1.5 rounded-full border border-transparent hover:border-[#b03121]/30">
                Panini con la Mortadella
              </RouterLink>
              <span className="text-[#E0D5C1] select-none shrink-0" aria-hidden="true">·</span>
              <RouterLink to="/lp/rotolo-60cm" onClick={trackClick('lp-subbar-rotoli')} data-testid="lp-subbar-rotoli"
                className="shrink-0 inline-flex items-center font-['Poppins',_sans-serif] font-semibold text-xs sm:text-sm text-[#1A1A1A] hover:text-[#b03121] hover:bg-[#b03121]/5 transition-colors px-3 py-1.5 rounded-full border border-transparent hover:border-[#b03121]/30">
                Rotoli Giganti
              </RouterLink>
              <span className="text-[#E0D5C1] select-none shrink-0" aria-hidden="true">·</span>
              <RouterLink to="/lp/vegan" onClick={trackClick('lp-subbar-vegan')} data-testid="lp-subbar-vegan"
                className="shrink-0 inline-flex items-center font-['Poppins',_sans-serif] font-semibold text-xs sm:text-sm text-[#1A1A1A] hover:text-[#b03121] hover:bg-[#b03121]/5 transition-colors px-3 py-1.5 rounded-full border border-transparent hover:border-[#b03121]/30">
                Vegani
              </RouterLink>
            </nav>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center pt-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMG.hero} alt="Sfarinà" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/35"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
          <div className="max-w-4xl">
            <p className="font-['Lora',_serif] text-base md:text-lg text-gray-200 mb-6 italic">{t.hero.tagline}</p>
            <div className="inline-block bg-[#FDFBF7] text-[#b03121] border-l-[6px] border-[#b03121] shadow-lg font-['Poppins',_sans-serif] font-extrabold px-5 py-3 text-lg md:text-2xl lg:text-3xl italic tracking-tight mb-8">{t.hero.banner}</div>
            <h1 className="font-['Poppins',_sans-serif] font-black text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white !leading-[1.15] mb-5 uppercase">
              {t.hero.h1_line1}<br />{t.hero.h1_line2}
            </h1>
            <h2 className="font-['Poppins',_sans-serif] font-bold italic text-xl md:text-2xl lg:text-3xl text-[#f5efe6] !leading-snug mb-10">{t.hero.h2}</h2>
            <div className="flex flex-col sm:flex-row gap-4" id="cta">
              <PrimaryCTA onClick={() => openModal('hero')} className="rounded-full text-lg px-8 py-4" testid="hero-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
              <a href="#menu" onClick={trackClick('hero-menu')} data-testid="hero-link-menu" className="bg-white text-[#1a1a1a] text-center font-['Poppins',_sans-serif] font-bold px-8 py-4 rounded-full text-lg hover:bg-gray-100 transition shadow-xl">{t.hero.btn_menu}</a>
            </div>
          </div>
        </div>
      </section>

      {/* 1. SEZIONE IDENTITÀ — Direct Response: title → 3 micro-card → divider → hook → paragraph card → conclusion+CTA card */}
      <section id="identita" className="py-20 md:py-28 bg-[#EDE8E0]/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Title + kicker */}
          <div className="text-center mb-12 md:mb-14">
            <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.3em] text-[#b03121] mb-4" data-testid="identita-kicker">{t.identita.kicker}</p>
            <h2 className="font-['Poppins',_sans-serif] font-bold text-4xl md:text-5xl text-[#1A1A1A] !leading-tight">
              {t.identita.title_pre} <span className="text-highlight">{t.identita.title_highlight}</span>
            </h2>
            <p className="font-['Lora',_serif] italic text-[#5a4a42] mt-4 text-base md:text-lg">{t.identita.list_intro}</p>
          </div>

          {/* 3 micro-card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-12 md:mb-14" data-testid="identita-cards">
            {t.identita.list.map((item, i) => (
              <div key={item} data-testid={`identita-card-${i}`} className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 md:p-6 flex items-start gap-4 transition-shadow hover:shadow-md">
                <span className="font-['Poppins',_sans-serif] font-black text-[#b03121]/25 text-3xl md:text-4xl leading-none shrink-0 w-10">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-['Poppins',_sans-serif] font-semibold text-base md:text-lg text-[#1A1A1A] !leading-snug pt-1">{item}</span>
              </div>
            ))}
          </div>

          {/* Divider elegante */}
          <div className="flex items-center justify-center gap-3 mb-10 md:mb-12 max-w-md mx-auto" aria-hidden="true">
            <span className="h-px flex-1 bg-[#E0D5C1]"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#b03121]/50"></span>
            <span className="h-px flex-1 bg-[#E0D5C1]"></span>
          </div>

          {/* Hook headline */}
          <p className="font-['Poppins',_sans-serif] font-bold italic text-2xl md:text-3xl text-[#b03121] !leading-snug text-center max-w-3xl mx-auto mb-10" data-testid="identita-question">
            {t.identita.question}
          </p>

          {/* Paragraph card + Conclusion+CTA card — 2 colonne su desktop, stack su mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto items-stretch">
            {/* Paragraph card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 md:p-8 flex flex-col" data-testid="identita-paragraph-card">
              <Sparkles className="text-[#b03121]/55 mb-3" size={24} />
              <p className="text-base md:text-lg text-[#333] leading-relaxed">{t.identita.paragraph}</p>
            </div>

            {/* Conclusion + CTA card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 md:p-8 text-center flex flex-col items-center justify-center" data-testid="identita-cta-card">
              <p className="font-['Poppins',_sans-serif] font-bold text-xl md:text-2xl text-[#1A1A1A] !leading-tight mb-6">{t.identita.conclusion}</p>
              <PrimaryCTA onClick={() => openModal('identita')} testid="identita-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SEZIONE A CONTRASTO SCURO — Direct Response: titolo → row testo+immagine → card full-width → CTA full-width */}
      <section className="py-24 bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Poppins',_sans-serif] font-bold text-3xl md:text-4xl lg:text-5xl !leading-tight mb-12 md:mb-16 text-center">
            {t.contrasto.title_pre} <span className="text-highlight">{t.contrasto.title_highlight}</span>
          </h2>

          {/* Row 1: testo (hook + 2 box) sinistra | immagine destra centrata verticalmente */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center mb-12 md:mb-16">
            <div className="md:col-span-7 md:py-4">
              {/* Hook visivo */}
              <div className="text-center md:text-left mb-10 md:mb-12">
                <div className="flex justify-center md:justify-start mb-6">
                  <div className="h-1 w-12 bg-[#b03121] rounded-full"></div>
                </div>
                <p className="text-xl md:text-2xl font-medium leading-relaxed text-white" data-testid="contrasto-hook">
                  {t.contrasto.hook_pre}<span className="text-highlight">{t.contrasto.hook_h}</span>{t.contrasto.hook_end}
                </p>
              </div>

              {/* 2 box affiancati con divider centrale */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 relative" data-testid="contrasto-boxes">
                <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-3/4 bg-white/15" aria-hidden="true"></div>
                <div className="px-1 md:pr-6">
                  <p className="text-white/85 text-base md:text-lg leading-relaxed">
                    {t.contrasto.box1_pre}<strong className="text-white">{t.contrasto.box1_h}</strong>{t.contrasto.box1_end}
                  </p>
                </div>
                <div className="px-1 md:pl-6 border-t border-white/15 md:border-none pt-6 md:pt-0">
                  <p className="text-white/85 text-base md:text-lg leading-relaxed">
                    {t.contrasto.box2_pre}<strong className="text-white">{t.contrasto.box2_h}</strong>{t.contrasto.box2_end}
                  </p>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="aspect-square w-full rounded-xl overflow-hidden shadow-2xl">
                <img src={IMG.detailDop} alt="Piadina Sfarinà" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Row 2 full-width: Card promessa */}
          <div className="p-6 md:p-10 bg-white text-stone-800 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.25)] border border-stone-100 relative overflow-hidden mb-8 md:mb-10 max-w-5xl mx-auto" data-testid="contrasto-promise-card">
            <span className="absolute -top-8 -left-2 text-[160px] text-stone-100 font-serif leading-none select-none" aria-hidden="true">&ldquo;</span>
            <div className="relative z-10 flex flex-col gap-5">
              <p className="text-stone-600 text-base md:text-lg leading-relaxed">{t.contrasto.card_p1}</p>
              <div className="w-8 h-px bg-[#b03121]/40 my-1"></div>
              <p className="text-stone-800 text-base md:text-lg font-medium leading-relaxed">
                {t.contrasto.card_p2_pre}<span className="bg-[#b03121]/10 text-[#b03121] px-1 rounded">{t.contrasto.card_p2_h}</span>
              </p>
            </div>
          </div>

          {/* Row 3 full-width: CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            <PrimaryCTA className="w-full sm:w-auto" onClick={() => openModal('contrasto')} testid="contrasto-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
            <SecondaryCTA text={t.contrasto.btn_menu} href="#menu" onClick={trackClick('contrasto-menu')} testid="contrasto-link-menu" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-[#1A1A1A]" />
          </div>
        </div>
      </section>

      {/* 4. SEZIONE PRODOTTI */}
      <section id="menu" className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-['Poppins',_sans-serif] font-bold text-5xl text-[#1A1A1A] mb-4">{t.menu.title}</h2>
          <p className="font-['Poppins',_sans-serif] text-sm sm:text-lg md:text-xl text-[#b03121] mb-6 whitespace-nowrap overflow-x-auto -mx-4 px-4" data-testid="menu-subtitle">{t.menu.subtitle}</p>
          <p className="text-xl text-[#5a4a42] leading-relaxed">{t.menu.intro_pre}<span className="text-highlight">{t.menu.intro_h}</span>{t.menu.intro_end}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
          {/* PIADINE */}
          <div className="flex flex-col">
            <ProductImage src={IMG.piadine} alt="Piadine" className="rounded-t-2xl w-full" aspect="aspect-[4/3] md:aspect-square" />
            <div className="bg-white p-8 rounded-b-2xl shadow-md flex-grow flex flex-col border border-t-0 border-[#EDE8E0]">
              <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl mb-4 text-[#1A1A1A]">{t.menu.piadine.title}</h3>
              <p className="text-[#333] text-lg mb-8 flex-grow">{t.menu.piadine.body_pre}<span className="text-highlight">{t.menu.piadine.body_h}</span>{t.menu.piadine.body_end}</p>
              <PrimaryCTA className="w-full" onClick={() => openModal('card-piadine')} testid="card-piadine-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
              <MenuLink href="#menu-piadine" label={t.menu.piadine.link} onClick={trackClick('menu-link-piadine', { target: 'menu-piadine' })} testid="menu-link-piadine" />
            </div>
          </div>

          {/* CRESCIONI */}
          <div className="flex flex-col">
            <ProductImage src={IMG.crescioni} alt="Crescioni" className="rounded-t-2xl w-full" aspect="aspect-[4/3] md:aspect-square" />
            <div className="bg-white p-8 rounded-b-2xl shadow-md flex-grow flex flex-col border border-t-0 border-[#EDE8E0]">
              <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl mb-4 text-[#1A1A1A]">{t.menu.crescioni.title}</h3>
              <p className="text-[#333] text-lg mb-8 flex-grow">{t.menu.crescioni.body_pre}<span className="text-highlight">{t.menu.crescioni.body_h}</span>{t.menu.crescioni.body_end}</p>
              <PrimaryCTA className="w-full" onClick={() => openModal('card-crescioni')} testid="card-crescioni-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
              <MenuLink href="#menu-crescioni" label={t.menu.crescioni.link} onClick={trackClick('menu-link-crescioni', { target: 'menu-crescioni' })} testid="menu-link-crescioni" />
            </div>
          </div>

          {/* I ROTOLI */}
          <div className="md:col-span-2 flex flex-col border-2 border-[#b03121] rounded-2xl overflow-hidden bg-white shadow-xl relative mt-8">
            <div className="flex flex-col lg:flex-row">
              <div className="w-full lg:w-1/2 min-h-[300px] relative">
                <img src={IMG.rotoli} alt="Rotoli" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="w-full lg:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                <h3 className="font-['Poppins',_sans-serif] font-bold text-4xl mb-6 text-[#b03121]">{t.menu.rotoli.title}</h3>
                <div className="space-y-4 text-lg text-[#333] mb-8">
                  <p>{t.menu.rotoli.p1_pre}<span className="text-highlight">{t.menu.rotoli.p1_h}</span>{t.menu.rotoli.p1_end}</p>
                  <p>{t.menu.rotoli.p2_pre}<span className="text-highlight">{t.menu.rotoli.p2_h}</span>{t.menu.rotoli.p2_end}</p>
                  <p className="font-['Poppins',_sans-serif] font-bold text-[#1A1A1A]">{t.menu.rotoli.p3_pre}<span className="text-highlight">{t.menu.rotoli.p3_h}</span></p>
                </div>
                <PrimaryCTA onClick={() => openModal('card-rotoli')} testid="card-rotoli-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
                <MenuLink href="#menu-rotoli" label={t.menu.rotoli.link} onClick={trackClick('menu-link-rotoli', { target: 'menu-rotoli' })} testid="menu-link-rotoli" />
              </div>
            </div>
          </div>

          {/* FRENCH TACOS — centered full-width */}
          <div className="md:col-span-2 max-w-2xl mx-auto w-full flex flex-col mt-8">
            <ProductImage src={IMG.tacos} alt="French Tacos" className="rounded-t-2xl w-full" aspect="aspect-[4/3] md:aspect-square" />
            <div className="bg-white p-8 rounded-b-2xl shadow-md flex-grow flex flex-col items-center text-center border border-t-0 border-[#EDE8E0]">
              <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl mb-4 text-[#1A1A1A]">{t.menu.tacos.title}</h3>
              <p className="text-[#333] text-lg mb-8 flex-grow">{t.menu.tacos.body_pre}<span className="text-highlight">{t.menu.tacos.body_h}</span>{t.menu.tacos.body_end}</p>
              <PrimaryCTA className="w-full" onClick={() => openModal('card-tacos')} testid="card-tacos-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
              <a href="#menu-tacos" onClick={trackClick('menu-link-tacos', { target: 'menu-tacos' })} data-testid="menu-link-tacos"
                className="inline-flex items-center gap-2 mt-4 text-[#b03121] hover:text-[#8e261a] font-['Poppins',_sans-serif] font-semibold text-base border-b-2 border-[#b03121]/40 hover:border-[#b03121] transition-colors pb-1 group">
                <span>{t.menu.tacos.link}</span>
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>

          {/* CHIUSURA — TRE ALTERNATIVE full-width */}
          <div className="md:col-span-2 w-full flex flex-col mt-8">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-md flex-grow flex flex-col items-center text-center border border-[#EDE8E0]">
              <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl md:text-4xl mb-4 text-[#1A1A1A]">{t.menu.chiusura.title}</h3>
              <p className="text-[#333] text-lg mb-8 flex-grow max-w-3xl">{t.menu.chiusura.body_pre}<span className="text-highlight">{t.menu.chiusura.body_h}</span>{t.menu.chiusura.body_end}</p>
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xl justify-center">
                <PrimaryCTA className="w-full sm:w-auto" onClick={() => openModal('card-chiusura')} testid="card-chiusura-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
                <SecondaryCTA text={t.menu.chiusura.btn_menu} href="#menu-dettaglio" onClick={trackClick('chiusura-link-menu')} testid="chiusura-link-menu" className="w-full sm:w-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. SEZIONE STORY / SEGRETO — Row 1: titolo+hook+bullets card | immagine centrata. Row 2+: quote, finale, conclusion, CTA full-width */}
      <section id="segreto" ref={segretoRef} className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Row 1: testo (titolo+tag+hook+bullets card) | immagine centrata verticalmente */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center mb-12 md:mb-16">
          {/* Immagine — su mobile appare prima per impatto visivo (order-1), su desktop a destra (order-2) */}
          <div className="order-1 md:order-2 md:col-span-5">
            <div className="aspect-[4/5] md:aspect-square w-full rounded-xl overflow-hidden shadow-2xl">
              <img src={IMG.segreto} alt={t.segreto.subtitle} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>

          {/* Blocco testo: titolo + tag + hook + bullets card */}
          <div className="order-2 md:order-1 md:col-span-7 flex flex-col gap-8 md:gap-10">
            <div>
              <h2 className="font-['Poppins',_sans-serif] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-3">{t.segreto.title}</h2>
              <h3 className="font-['Poppins',_sans-serif] font-bold text-xl md:text-2xl text-[#b03121] uppercase tracking-wide">{t.segreto.subtitle}</h3>
            </div>

            {/* Tag + Hook con underline */}
            <div data-testid="segreto-hook">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-8 bg-[#b03121]"></div>
                <span className="text-xs font-['Poppins',_sans-serif] font-bold tracking-widest text-[#b03121] uppercase">{t.segreto.tag}</span>
              </div>
              <p className="text-xl md:text-2xl font-medium leading-relaxed text-stone-700">
                {t.segreto.hook_pre}<span className="text-stone-900 border-b-2 border-[#b03121]/40">{t.segreto.hook_h}</span>
              </p>
            </div>

            {/* Bullets card INGRANDITA */}
            <div className="bg-white p-7 md:p-9 rounded-xl border border-stone-100 shadow-[0_4px_20px_rgb(0,0,0,0.05)] flex flex-col md:flex-row gap-7 md:gap-9 items-start" data-testid="segreto-bullets-card">
              <div className="flex-shrink-0 flex flex-col gap-4 font-semibold text-stone-600">
                {t.segreto.bullets.map((b, i) => (
                  <div key={b} className="flex items-center gap-3 text-base md:text-lg" data-testid={`segreto-bullet-${i}`}>
                    <X className="w-6 h-6 text-[#b03121] shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
              <div className="w-full md:w-px h-px md:h-auto md:self-stretch bg-stone-200" aria-hidden="true"></div>
              <div className="flex-1">
                <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
                  <strong className="text-stone-900 text-xl md:text-2xl font-['Poppins',_sans-serif] block mb-2">{t.segreto.bullets_text_h}</strong>
                  <span className="text-base md:text-lg">{t.segreto.bullets_text_end}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 full-width: Quote emotivo */}
        <div className="relative pl-6 md:pl-10 border-l-4 border-[#b03121] mb-10 md:mb-12 max-w-4xl mx-auto" data-testid="segreto-quote">
          <p className="text-xl md:text-2xl text-[#1A1A1A] leading-relaxed font-medium italic">
            {t.segreto.quote_pre}<span className="not-italic font-bold">{t.segreto.quote_h}</span>{t.segreto.quote_end}
          </p>
        </div>

        {/* Row 3 full-width: Paragrafo finale con highlight */}
        <div className="mb-10 md:mb-12 max-w-4xl mx-auto" data-testid="segreto-final">
          <p className="text-base md:text-lg text-stone-600 leading-relaxed">
            {t.segreto.final_pre}<strong className="text-stone-900">{t.segreto.final_h1}</strong>{t.segreto.final_mid}<span className="bg-[#b03121]/10 font-medium text-[#1A1A1A] px-1">{t.segreto.final_h2}</span>{t.segreto.final_end}
          </p>
        </div>

        {/* Row 4 full-width: Conclusion + CTA */}
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-['Poppins',_sans-serif] font-bold text-[#1A1A1A] text-xl md:text-2xl mb-6">{t.segreto.conclusion}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PrimaryCTA className="w-full sm:w-auto" onClick={() => openModal('segreto')} testid="segreto-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
            <SecondaryCTA text={t.segreto.btn_menu} href="#menu" onClick={trackClick('segreto-menu')} testid="segreto-link-menu" className="w-full sm:w-auto" />
          </div>
        </div>
      </section>

      {/* 5. TABELLA COMPARATIVA — nuovo componente responsive */}
      <ComparisonTable />

      {/* 6. SEZIONE SEDI */}
      <section id="sedi" ref={sediRef} className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <h2 className="font-['Poppins',_sans-serif] font-bold text-5xl text-[#1A1A1A] text-center mb-16">{t.sedi.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* SEDE 1 - Mercato delle Erbe */}
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#EDE8E0] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-[#b03121]"></div>
            <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl text-[#1A1A1A] mb-4 pl-4">{t.sedi.mercato.name}</h3>
            <div className="pl-4 space-y-6">
              <div className="flex items-start gap-3">
                <MapPin className="text-[#b03121] shrink-0 mt-1" />
                <div>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="sede1-address">{t.sedi.mercato.address}</p>
                  <p className="text-sm text-[#9a9080] font-['Poppins',_sans-serif] italic mt-0.5" data-testid="sede1-subtitle">{t.sedi.mercato.subtitle}</p>
                </div>
              </div>
              <p className="text-[#5a4a42] text-lg font-['Poppins',_sans-serif]">{t.sedi.mercato.offer}</p>
              <div className="bg-[#EDE8E0]/50 p-4 rounded-lg text-sm text-[#5a4a42] border border-[#E0D5C1]">
                <p className="font-['Poppins',_sans-serif] font-bold uppercase tracking-wider text-xs text-[#1A1A1A] mb-3">{t.sedi.hours_title}</p>
                <SfarinaHours days={[
                  { d: 'sun', h: '10:30–15:30 · 18:30–23' },
                  { d: 'mon', h: '10:30–15:30 · 18:30–23' },
                  { d: 'tue', h: '10:30–15:30 · 18:30–23' },
                  { d: 'wed', h: '10:30–15:30 · 18:30–23' },
                  { d: 'thu', h: '10:30–23' },
                  { d: 'fri', h: '10:30–00:30' },
                  { d: 'sat', h: '10:30–01' },
                ]} dayLabels={t.sedi.days} todayLabel={t.sedi.today} />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href="https://maps.app.goo.gl/qVjxChpNPZpRAFts7" target="_blank" rel="noopener noreferrer"
                  onClick={trackClick('sede1-maps', { sede: 'mercato-erbe' })} data-testid="sede1-maps"
                  className="flex-1 bg-[#1A1A1A] hover:bg-[#333] text-white font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors flex justify-center items-center gap-2">
                  <MapPin size={18} /> {t.sedi.btn_maps}
                </a>
                <a href="tel:+393423315759" onClick={() => sendServerSideEvent('CallIntent', { sede: 'mercato-erbe', phone: '+393423315759' })} data-testid="sede1-call"
                  className="flex-1 border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors flex justify-center items-center gap-2">
                  <Phone size={18} /> {t.sedi.btn_call}
                </a>
              </div>
            </div>
          </div>

          {/* SEDE 2 - Cefalonia */}
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#EDE8E0] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-[#b03121]"></div>
            <div className="absolute top-4 right-4 bg-[#b03121] text-white font-['Poppins',_sans-serif] font-bold px-3 py-1 rounded text-xs uppercase tracking-wide">{t.sedi.cefalonia.badge}</div>
            <h3 className="font-['Poppins',_sans-serif] font-bold text-3xl text-[#1A1A1A] mb-4 pl-4">{t.sedi.cefalonia.name}</h3>
            <div className="pl-4 space-y-6">
              <div className="flex items-start gap-3">
                <MapPin className="text-[#b03121] shrink-0 mt-1" />
                <div>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="sede2-address">{t.sedi.cefalonia.address}</p>
                  <p className="text-sm text-[#9a9080] font-['Poppins',_sans-serif] italic mt-0.5" data-testid="sede2-subtitle">{t.sedi.cefalonia.subtitle}</p>
                </div>
              </div>
              <p className="text-[#5a4a42] text-lg font-['Poppins',_sans-serif]">{t.sedi.cefalonia.offer}</p>
              <div className="bg-[#EDE8E0]/50 p-4 rounded-lg text-sm text-[#5a4a42] border border-[#E0D5C1]">
                <p className="font-['Poppins',_sans-serif] font-bold uppercase tracking-wider text-xs text-[#1A1A1A] mb-3">{t.sedi.hours_title}</p>
                <SfarinaHours days={[
                  { d: 'sun', h: '10:30–19:30' },
                  { d: 'mon', h: '10:30–19' },
                  { d: 'tue', h: '10:30–21:30' },
                  { d: 'wed', h: '10:30–21:30' },
                  { d: 'thu', h: '10:30–21:30' },
                  { d: 'fri', h: '10:30–01' },
                  { d: 'sat', h: '10:30–22:30' },
                ]} dayLabels={t.sedi.days} todayLabel={t.sedi.today} />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href="https://maps.app.goo.gl/izYf5L7TyBuuyvk7A" target="_blank" rel="noopener noreferrer"
                  onClick={trackClick('sede2-maps', { sede: 'cefalonia' })} data-testid="sede2-maps"
                  className="flex-1 bg-[#1A1A1A] hover:bg-[#333] text-white font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors flex justify-center items-center gap-2">
                  <MapPin size={18} /> {t.sedi.btn_maps}
                </a>
                <a href="tel:+393423315759" onClick={() => sendServerSideEvent('CallIntent', { sede: 'cefalonia', phone: '+393423315759' })} data-testid="sede2-call"
                  className="flex-1 border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors flex justify-center items-center gap-2">
                  <Phone size={18} /> {t.sedi.btn_call}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CTA FINALE */}
      <section id="sconto" ref={ctaFinalRef} className="py-24 bg-[#1A1A1A] text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Poppins',_sans-serif] font-bold text-5xl md:text-6xl text-white mb-6 !leading-tight">{t.cta_final.title}</h2>
          <p className="text-2xl text-[#EDE8E0] font-light mb-12">{t.cta_final.subtitle}</p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <PrimaryCTA onClick={() => openModal('cta-finale')} testid="ctafinale-cta-discount">{t.nav.cta_discount}</PrimaryCTA>
            <a href="#sedi" onClick={trackClick('ctafinale-link-sedi')} data-testid="ctafinale-link-sedi" className="text-white hover:text-[#b03121] font-['Poppins',_sans-serif] font-bold border-b-2 border-transparent hover:border-[#b03121] transition-colors pb-1">{t.cta_final.link_sedi}</a>
            <a href="#menu" onClick={trackClick('ctafinale-link-menu')} data-testid="ctafinale-link-menu" className="text-white hover:text-[#b03121] font-['Poppins',_sans-serif] font-bold border-b-2 border-transparent hover:border-[#b03121] transition-colors pb-1">{t.cta_final.link_menu}</a>
          </div>
        </div>
      </section>

      {/* MENÙ DETTAGLIATO — con link PDF */}
      <section id="menu-dettaglio" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#FDFBF7] border-t border-[#EDE8E0]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.3em] text-[#b03121] mb-4">{t.menu_dettaglio.kicker}</p>
            <h2 className="font-['Poppins',_sans-serif] font-black text-4xl md:text-5xl text-[#1A1A1A] mb-4">{t.menu_dettaglio.title_pre}<span className="text-highlight">{t.menu_dettaglio.title_h}</span></h2>
            <p className="font-['Lora',_serif] text-lg text-[#5a4a42] max-w-2xl mx-auto">{t.menu_dettaglio.intro}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { id: 'menu-piadine', title: t.menu_dettaglio.piadine_title, body: t.menu_dettaglio.piadine_body, pdf: PDF.piadine, kind: 'piadine' },
              { id: 'menu-crescioni', title: t.menu_dettaglio.crescioni_title, body: t.menu_dettaglio.crescioni_body, pdf: PDF.crescioni, kind: 'crescioni' },
            ].map((cat) => (
              <div key={cat.id} id={cat.id} data-testid={`section-${cat.id}`} className="scroll-mt-28 bg-white border border-[#EDE8E0] rounded-2xl p-8 shadow-sm flex flex-col">
                <h3 className="font-['Poppins',_sans-serif] font-bold text-2xl text-[#b03121] mb-2">{cat.title}</h3>
                <p className="font-['Lora',_serif] text-[#333] leading-relaxed flex-grow mb-6">{cat.body}</p>
                <a href={cat.pdf} target="_blank" rel="noopener noreferrer" onClick={trackPdfClick(cat.kind)} data-testid={`pdf-${cat.kind}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#b03121] text-white font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors self-start">
                  <Download size={18} /> {t.menu_dettaglio.view_pdf}
                </a>
              </div>
            ))}

            <div id="menu-rotoli" data-testid="section-menu-rotoli" className="scroll-mt-28 bg-white border-2 border-[#b03121] rounded-2xl p-8 shadow-md md:col-span-2 flex flex-col">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="bg-[#1A1A1A] text-white font-['Poppins',_sans-serif] font-bold px-3 py-1 rounded-full text-[10px] uppercase tracking-widest">{t.menu_dettaglio.protagonista}</span>
                <h3 className="font-['Poppins',_sans-serif] font-bold text-2xl text-[#b03121]">{t.menu_dettaglio.rotoli_title}</h3>
              </div>
              <p className="font-['Lora',_serif] text-[#333] leading-relaxed flex-grow mb-6">{t.menu_dettaglio.rotoli_body}</p>
              <a href={PDF.rotoli} target="_blank" rel="noopener noreferrer" onClick={trackPdfClick('rotoli')} data-testid="pdf-rotoli"
                className="inline-flex items-center justify-center gap-2 bg-[#b03121] hover:bg-[#8e261a] text-white font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors self-start">
                <Download size={18} /> {t.menu_dettaglio.view_pdf}
              </a>
            </div>

            <div id="menu-tacos" data-testid="section-menu-tacos" className="scroll-mt-28 bg-white border border-[#EDE8E0] rounded-2xl p-8 shadow-sm md:col-span-2 flex flex-col">
              <h3 className="font-['Poppins',_sans-serif] font-bold text-2xl text-[#b03121] mb-2">{t.menu_dettaglio.tacos_title}</h3>
              <p className="font-['Lora',_serif] text-[#333] leading-relaxed flex-grow mb-6">{t.menu_dettaglio.tacos_body}</p>
              <a href={PDF.tacos} target="_blank" rel="noopener noreferrer" onClick={trackPdfClick('tacos')} data-testid="pdf-tacos"
                className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#b03121] text-white font-['Poppins',_sans-serif] font-bold py-3 px-6 rounded-lg transition-colors self-start">
                <Download size={18} /> {t.menu_dettaglio.view_pdf}
              </a>
            </div>
          </div>

          <div className="text-center mt-14">
            <PrimaryCTA onClick={() => openModal('menu-dettaglio')} testid="menudettaglio-cta-discount" className="rounded-full text-lg px-8 py-4">{t.menu_dettaglio.cta}</PrimaryCTA>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />

      <LeadFormModal open={modalOpen} onClose={() => setModalOpen(false)} source={modalSource} />
    </div>
  );
}
